import { ReactNode } from 'react';
import { useTranslation } from '../../i18n/useTranslation';

export interface NavItem {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  active?: boolean;
  badge?: number;
  ariaLabel: string;
}

interface MobileBottomNavProps {
  items: NavItem[];
  className?: string;
}

export function MobileBottomNav({ items, className = '' }: MobileBottomNavProps) {
  const { t } = useTranslation();
  return (
    <nav
      className={`fixed inset-x-0 bottom-0 bg-white border-t border-neutral-200
                 shadow-lg md:hidden ${className}`}
      style={{
        zIndex: 'var(--z-bottom-nav)',
        height: 'var(--bottom-nav-height)',
        paddingBottom: 'env(safe-area-inset-bottom)'
      }}
      role="navigation"
      aria-label={t('App.Common.MainNavigation')}
    >
      <div className="flex items-center justify-around h-full px-2">
        {items.map((item) => (
          <button
            key={item.id}
            onClick={item.onClick}
            className={`
              relative flex flex-col items-center justify-center gap-1
              rounded-lg transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
              ${item.active
                ? 'text-primary-600'
                : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
              }
            `}
            style={{
              minWidth: 'var(--touch-target-min)',
              minHeight: 'var(--touch-target-min)',
              padding: '0.5rem'
            }}
            aria-label={item.ariaLabel}
            aria-current={item.active ? 'page' : undefined}
          >
            {/* Icon */}
            <span className="relative">
              {item.icon}

              {/* Badge */}
              {item.badge !== undefined && item.badge > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 bg-error-500 text-white
                           text-xs font-bold rounded-full flex items-center justify-center"
                  aria-label={`${item.badge} ${item.label.toLowerCase()}`}
                >
                  {item.badge > 9 ? '9+' : item.badge}
                </span>
              )}
            </span>

            {/* Label */}
            <span className="text-xs font-medium whitespace-nowrap">
              {item.label}
            </span>

            {/* Active indicator */}
            {item.active && (
              <span
                className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-1
                         bg-primary-600 rounded-full"
                aria-hidden="true"
              />
            )}
          </button>
        ))}
      </div>
    </nav>
  );
}
